import { FC } from "react";
import { useNavigate, NavigateFunction } from "react-router-dom";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSuitcase, faPersonDress } from "@fortawesome/free-solid-svg-icons";
import colors from "../../styles/colors";
import type { Wrestler } from "../../types";

const Container = styled.div<{ $active: boolean }>`
    & .cont-box-avatar {
        position: relative;
        background: linear-gradient(
            160deg,
            rgba(163, 27, 27, 0.12) 0%,
            rgba(0, 0, 0, 0.25) 100%
        );
        color: white;
        border: 1px solid rgba(163, 27, 27, ${({ $active }) => $active ? '0.35' : '0.12'});
        border-radius: 10px;
        padding-top: 20px;
        transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
        opacity: ${({ $active }) => $active ? '1' : '0.55'};
    }

    & .content-card:hover {
        cursor: pointer;
        transform: translateY(-4px);
        border-color: rgba(163, 27, 27, 0.65) !important;
        box-shadow: 0 8px 28px rgba(163, 27, 27, 0.15), 0 4px 12px rgba(0,0,0,0.4);
        opacity: 1 !important;
    }
`;


const BadgeArea = styled.div`
    position: absolute;
    top: 14px;
    right: 6px;
    display: flex;
    flex-direction: column;
    gap: 3px;
    z-index: 2;
`;

const Badge = styled.span<{ color: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background-color: ${props => props.color};
    border: 1px solid rgba(255,255,255,0.3);
    font-size: 10px;
    color: white;

    svg {
        width: 10px;
        height: 10px;
    }
`;

const InactiveBadge = styled.span`
    display: inline-block;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.5px;
    color: rgba(255,255,255,0.7);
    background-color: rgba(0,0,0,0.55);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 3px;
    padding: 2px 3px;
    white-space: nowrap;
`;

const BoxAvatar = styled.div`
    display: flex;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 5px;
    border-radius: 10px;

    & .box-logo {
        width: 90%;
        aspect-ratio: 1;
    }

    & img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 8px;
    }
`;

const BoxOtherInfo = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    & p, h6 {
        padding: 0 10px;
        max-width: 100%;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    & p.weight, p.height {
        font-size: 14px;
    }
`;

interface SingleCardWrestlerProps {
    wrestler: Wrestler;
    switchIsLoading: (loading: boolean) => void;
}

const SingleCardWrestler: FC<SingleCardWrestlerProps> = (props) => {
    const wrestler = props.wrestler;
    const navigate: NavigateFunction = useNavigate();

    function navigateGoToWrestlerPage(id: number, name: string): void {
        props.switchIsLoading(true);
        const url = '/wrestlers/i_' + id + '_d_' + name;
        navigate(url);
    }

    return (
        <Container
            className="col-6 col-md-3 col-lg-2 mb-2"
            $active={!!wrestler.is_active}
            onClick={() => navigateGoToWrestlerPage(wrestler.id, wrestler.name)}
        >
            <div className="cont-box-avatar w-100 d-flex flex-column justify-content-center content-card">

                <BadgeArea>
                    {wrestler.is_manager ? (
                        <Badge color={colors.bgColorBlack} title="Manager">
                            <FontAwesomeIcon icon={faSuitcase} />
                        </Badge>
                    ) : null}
                    {wrestler.is_female ? (
                        <Badge color="rgb(140, 60, 120)" title="Donna">
                            <FontAwesomeIcon icon={faPersonDress} />
                        </Badge>
                    ) : null}
                    {!wrestler.is_active ? (
                        <InactiveBadge title="Ritirato">RIT</InactiveBadge>
                    ) : null}
                </BadgeArea>

                <BoxAvatar className="BoxAvatar-SingleCardWrestler d-flex justify-content-center">
                    <div className="box-logo">
                        <img
                            src={process.env.REACT_APP_STORAGE_URL + wrestler.avatar_path}
                            alt={wrestler.name}
                        />
                    </div>
                </BoxAvatar>

                <BoxOtherInfo className="BoxOtherInfo-SingleCardWrestler d-flex justify-content-center flex-column">
                    <h6 className="m-0">{wrestler.name}</h6>
                    <p className="m-0 place_birth">{wrestler.place_birth ?? '-'}</p>
                    <p className="m-0 weight">{wrestler.weight ?? '-'} kg</p>
                    <p className="m-0 height">{wrestler.height ?? '-'} cm</p>
                </BoxOtherInfo>

            </div>
        </Container>
    );
};

export default SingleCardWrestler;
